import { X } from 'lucide-react';

/**
 * Inner segmented Candidate / Contact / Both toggle for a queued
 * placement. Pulled out of SendDocumentPage so the same UX can be
 * reused (and so the disabled-state logic lives in one place).
 */
const RecipientToggle = ({ placement, onChange, allowCandidate = true, allowContact = true }) => {
  const recipientType = placement.recipient_type || 'candidate';
  const hasContact = !!(placement.contact_email || placement.contact_name);
  const candidateEnabled = allowCandidate;
  const contactEnabled = allowContact && hasContact;
  const canBoth = allowCandidate && allowContact && hasContact && !!placement.candidate_email;

  const buttonClass = (active, enabled = true) =>
    `text-[10px] px-2 py-0.5 rounded transition-colors ${
      active
        ? 'bg-[#1a4a5e] text-white shadow-sm'
        : enabled
          ? 'text-slate-600 hover:text-slate-900'
          : 'text-slate-400 cursor-not-allowed'
    }`;

  return (
    <div
      className="flex items-center bg-slate-100 rounded-md p-0.5 ml-1"
      data-testid={`recipient-toggle-${placement.id}`}
    >
      <button
        type="button"
        onClick={() => onChange(placement.id, 'candidate')}
        disabled={!candidateEnabled}
        className={buttonClass(recipientType === 'candidate', candidateEnabled)}
        data-testid={`recipient-candidate-${placement.id}`}
        title={!allowCandidate ? 'This document is not set up to send to the candidate' : placement.candidate_name ? `Send to candidate: ${placement.candidate_name}` : 'Send to candidate'}
      >
        Candidate
      </button>
      <button
        type="button"
        onClick={() => onChange(placement.id, 'contact')}
        disabled={!contactEnabled}
        className={buttonClass(recipientType === 'contact', contactEnabled)}
        data-testid={`recipient-contact-${placement.id}`}
        title={!allowContact ? 'This document is not set up to send to the client contact' : hasContact ? `Send to client contact: ${placement.contact_name}` : 'No client contact linked to this placement in Bullhorn'}
      >
        Contact
      </button>
      <button
        type="button"
        onClick={() => onChange(placement.id, 'both')}
        disabled={!canBoth}
        className={buttonClass(recipientType === 'both', canBoth)}
        data-testid={`recipient-both-${placement.id}`}
        title={!allowCandidate || !allowContact ? 'This document only goes to one side — Candidate or Contact' : canBoth ? 'Send one document to each — candidate and contact' : 'Both candidate and contact must have email addresses in Bullhorn'}
      >
        Both
      </button>
    </div>
  );
};

/**
 * Single queued placement chip with email status + 3-way recipient
 * toggle + remove button. Pure presentational — all state lives in
 * the parent.
 */
export const PlacementChip = ({ placement, onChangeRecipientType, onRemove, allowCandidate = true, allowContact = true }) => (
  <div
    className="inline-flex items-center gap-2 text-xs bg-white border border-[#1a4a5e]/30 rounded-lg pl-3 pr-1 py-1.5"
    data-testid={`selected-placement-${placement.id}`}
  >
    <span className="font-mono text-[10px] text-[#1a4a5e] bg-[#1a4a5e]/10 px-1.5 py-0.5 rounded">
      P{placement.id}
    </span>
    <span className="font-medium text-slate-800">{placement.name}</span>
    {placement.email
      ? <span className="text-muted-foreground">· {placement.email}</span>
      : (
        <span
          className="text-rose-600 text-[10px] font-medium"
          data-testid={`missing-email-${placement.id}`}
        >
          · no email
        </span>
      )
    }
    <RecipientToggle placement={placement} onChange={onChangeRecipientType} allowCandidate={allowCandidate} allowContact={allowContact} />
    <button
      type="button"
      onClick={() => onRemove(placement.id)}
      className="ml-1 w-5 h-5 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600"
      aria-label={`Remove ${placement.name}`}
      data-testid={`remove-placement-${placement.id}`}
    >
      <X className="w-3 h-3" />
    </button>
  </div>
);
